import { useMutation, useQuery } from "@apollo/client";
import { BaseTable, Button, Flexbox, Select } from "@sede-x/shell-ds-react-framework";
import { loader } from "graphql.macro";
import { useState } from "react";
import { ApolloErrorViewer } from "shared/components/ApolloErrorViewer";
import InlineLoadingPanel from "shared/components/InlineLoadingPanel";
import { usePicklists } from "ticketing/contexts/picklists/PicklistContextProvider";
import {
  GqlResponse,
  Picklists,
  TMovement,
  TMovementGroup,
  TPicklists
} from "ticketing/ticketing.types";
import { equalsIgnoreCase } from "ticketing/utils";

type DropDownListCellProps = {
  row: { original: TMovementMeasureGroup };
  column: { id: string };
  onChange: (updatedRow: TMovementMeasureGroup) => void;
  data?: Picklists.TUnitOfMeasure[] | Picklists.PMeasurementType[];
};

type MeasureOptionValue = {
  id: string | number;
  name: string;
};

type CellProps = {
  row: { original: TMovementMeasureGroup };
  column: { id: string };
};

const DropDownListCell = ({ row, column, onChange, data }: DropDownListCellProps) => {
  const isInEdit = row.original.inEdit;
  const field = column.id;
  const value = row.original[field as keyof TMovementMeasureGroup];

  // Handle null or undefined data safely
  const options = data || [];

  const handleChange = (newValue: string | number) => {
    const selectedValue = options.find(item => String(item.id) === String(newValue));

    onChange({
      ...row.original,
      [field]: selectedValue // Update the field with the selected value
    });
  };

  return (
    <td>
      {isInEdit ? (
        <Select
          value={(value as MeasureOptionValue)?.id?.toString() || ""}
          onChange={handleChange}
          allowClear={false}
          getPopupContainer={() => document.querySelector(".movement-aspect") || document.body}>
          {options.map(item => (
            <option key={item.id} value={item.id.toString()}>
              {item.name}
            </option>
          ))}
        </Select>
      ) : (
        <span>{(value as MeasureOptionValue)?.name || "N/A"}</span>
      )}
    </td>
  );
};

const QUERY_MOVEMENT_MEASURES = loader("../../ticketing-graphql/measuresOfMovement.graphql");

const UPDATE_MOVEMENT_MEASURES = loader(
  "../../ticketing-graphql/updateMeasuresOfMovements.graphql"
);
type Response = GqlResponse<Partial<TMovement[]>, "movements">;

type TMovementMeasureId = {
  id?: string;
  version?: number;
  movementId?: string;
  movementVersion?: number;
};

type TMovementMeasureGroup = {
  id: string;
  measurementType?: Picklists.PMeasurementType;
  unitOfMeasure?: Picklists.TUnitOfMeasure;
  value?: number;
  sortId: number;
  movementMeasureId?: [TMovementMeasureId];
  inEdit: boolean;
};

type TMovementMeasureUpdate = {
  id: string;
  version: number;
  measurementTypeId: number;
  unitOfMeasureId: number;
  value: number;
};

type TMovementMeasureInputUpdate = {
  id: string;
  version: number;
  measures: TMovementMeasureUpdate[];
};

const transformInput = (movements: Partial<TMovement[]>): TMovementMeasureGroup[] => {
  //Get all measures from alll sides of the movement
  const measures = movements.flatMap(m =>
    // m &&
    m?.measures?.map(mm => ({
      ...mm,
      movementId: m.id,
      movementVersion: m.version
    }))
  );

  //group measurement groups by measurement type id
  const measureGroups = measures.reduce<Record<string, TMovementMeasureGroup>>(
    (group, item) => {
      const measurementTypeId: string = `Id:${item!.measurementType.id}`;
      if (group[measurementTypeId] == null) {
        group[measurementTypeId] = {
          id: measurementTypeId,
          measurementType: item?.measurementType,
          unitOfMeasure: item?.unitOfMeasure,
          value: item?.value,
          movementMeasureId: [
            {
              id: item?.id,
              version: item?.version,
              movementId: item?.movementId,
              movementVersion: item?.movementVersion
            }
          ],
          sortId: equalsIgnoreCase(item?.measurementType?.name, "Temperature") ? 0 : +item?.id!,
          inEdit: !equalsIgnoreCase(item?.measurementType?.name, "Temperature")
        };
        return group;
      }
      group[measurementTypeId].movementMeasureId?.push({
        id: item?.id,
        version: item?.version
      });

      return group;
    },
    {}
  );

  return Object.values(measureGroups).sort((g1, g2) => g1.sortId - g2.sortId);
};

const transformOutput = (movementMeasures: TMovementMeasureGroup[]) => {
  const movementMeasureUpdate = movementMeasures?.flatMap(m =>
    m.movementMeasureId?.map(mm => ({
      id: mm.movementId,
      version: mm.movementVersion,
      measures: {
        id: mm.id!,
        version: mm.version!,
        measurementTypeId: m.measurementType?.id!,
        unitOfMeasureId: m.unitOfMeasure?.id!,
        value: m.value!
      }
    }))
  );

  const movementMeasureUpdateGroup = movementMeasureUpdate?.reduce<
    Record<string, TMovementMeasureInputUpdate>
  >((movementIdGroup, item) => {
    if (movementIdGroup[item!.id!] == null) {
      movementIdGroup[item!.id!] = {
        id: item?.id!,
        version: item?.version!,
        measures: [item?.measures!]
      };
      return movementIdGroup;
    }
    movementIdGroup[item!.id!].measures.push(item?.measures!);
    return movementIdGroup;
  }, {});

  return Object.values(movementMeasureUpdateGroup);
};

type MovementMeasuresProps = {
  movementGroup: TMovementGroup;
  editDisabled: boolean;
  onClose: () => void;
};

/**
 *
 * @param param0
 * @returns
 */

const columns = (
  picklists: TPicklists,
  handleItemChange: (updatedRow: TMovementMeasureGroup) => void
) => [
  {
    header: "Measurement Type",
    accessorKey: "measurementType",
    cell: (props: CellProps) => (
      <DropDownListCell
        row={props.row}
        column={props.column}
        onChange={handleItemChange}
        data={picklists?.measurementTypes}
      />
    )
  },
  {
    header: "Unit of Measure",
    accessorKey: "unitOfMeasure",
    cell: (props: CellProps) => (
      <DropDownListCell
        row={props.row}
        column={props.column}
        onChange={handleItemChange}
        data={picklists?.unitOfMeasures}
      />
    )
  },
  {
    header: "Value",
    accessorKey: "value"
  }
];

export const MovementMeasures = ({
  movementGroup,
  editDisabled,
  onClose
}: MovementMeasuresProps) => {
  const { picklists } = usePicklists();
  const [movementMeasures, setMovementMeasures] = useState<TMovementMeasureGroup[]>();

  const { loading, error, refetch } = useQuery<Response>(QUERY_MOVEMENT_MEASURES, {
    fetchPolicy: "no-cache",
    variables: { ids: movementGroup.movements.map(m => m.id) },
    onCompleted: data => onCompleted(data.movements)
  });

  const [updateMeasures, { loading: loadingMeasures, error: errorMeasures }] =
    useMutation<Response>(UPDATE_MOVEMENT_MEASURES, {
      onCompleted: data => {
        onCompleted(data.movements);
      }
    });

  const onCompleted = (movements: Partial<TMovement[]>) => {
    setMovementMeasures(transformInput(movements));
  };

  const onReset = () => {
    refetch({ ids: movementGroup.movements.map(m => m.id) });
  };

  // Convert the itemChange function to work with DropDownListCell
  const handleItemChange = (updatedRow: TMovementMeasureGroup) => {
    setMovementMeasures(measures =>
      measures?.map(m => (m.id === updatedRow.id ? updatedRow : m))
    );
  };

  const onSave = () => {
    if (movementMeasures) {
      const measures = transformOutput(movementMeasures);
      updateMeasures({
        variables: {
          measures
        }
      });
    }
  };

  const onSaveAndClose = () => {
    onSave();
    onClose();
  };
  if (loading || loadingMeasures) {
    return <InlineLoadingPanel />;
  }
  if (error) {
    return <ApolloErrorViewer error={error} />;
  }
  if (errorMeasures) {
    return <ApolloErrorViewer error={errorMeasures} />;
  }

  return (
    <Flexbox
      flexDirection="column"
      style={{
        height: "100%",
        overflow: "hidden"
      }}>
      <Flexbox
        justifyContent="space-between"
        alignItem="center"
        style={{
          padding: "8px",
          borderBottom: "1px solid #e0e0e0"
        }}>
        <div>
          <Button size="small" disabled={editDisabled} onClick={onReset}>
            Reset
          </Button>
        </div>

        <Flexbox gap="8px">
          <Button
            size="small"
            sentiment="positive"
            onClick={onSave}
            disabled={editDisabled || (movementMeasures?.length ?? 0) <= 0}>
            Save
          </Button>
          <Button
            size="small"
            sentiment="positive"
            onClick={onSaveAndClose}
            disabled={editDisabled || (movementMeasures?.length ?? 0) <= 0}>
            Save & Close
          </Button>
          <Button sentiment="negative" size="small" onClick={onClose}>
            Close
          </Button>
        </Flexbox>
      </Flexbox>

      <BaseTable
        data={movementMeasures}
        columns={
          picklists ? columns(picklists, updatedRow => handleItemChange(updatedRow)) : []
        }
        style={{ width: "100%" }}
      />
    </Flexbox>
  );
};
